import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Login() {
    const { login } = useAuth();
    const branding = useBranding();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await login(email, password);
            toast.success(`Welcome back, ${u.name}`);
            nav(u.role === "admin" ? "/admin" : u.role === "seeker" ? "/seeker/jobs" : u.role === "interviewer" ? "/interviewer" : "/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            <div className="hidden lg:flex flex-col justify-between p-12 bg-ikb text-white relative overflow-hidden">
                <Link to="/" className="flex items-center gap-2 relative z-10" data-testid="back-home">
                    <div className="w-7 h-7 bg-white flex items-center justify-center">
                        <span style={{ color: branding.primary_color || "#002FA7" }} className="font-display font-black text-sm">{(branding.short_name || "P")[0]}</span>
                    </div>
                    <span className="font-display font-black text-lg">{branding.short_name}</span>
                </Link>
                <div className="relative z-10">
                    <div className="text-xs uppercase tracking-widest opacity-70 font-mono mb-4">A note from the team</div>
                    <h2 className="font-display font-black text-4xl leading-tight">
                        Screening should be a decision, not a chore.
                    </h2>
                    <p className="mt-6 text-white/80 max-w-md">
                        {branding.product_name} scores, summarises and flags every resume against your job description — so you only ever
                        interview the candidates worth your time.
                    </p>
                </div>
                <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-white/5 rounded-full" />
                <div className="absolute top-1/3 -left-20 w-80 h-80 border border-white/10" />
            </div>
            <div className="flex items-center justify-center p-5 sm:p-8 bg-background">
                <div className="w-full max-w-md">
                    <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6 lg:hidden">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </Link>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb mb-2">Sign in</div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl mb-6 sm:mb-8">Welcome back.</h1>
                    <form onSubmit={submit} className="space-y-5">
                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-widest font-bold">Email</Label>
                            <Input
                                id="email" type="email" required autoFocus
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                className="rounded-none mt-2 h-11" data-testid="login-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-widest font-bold">Password</Label>
                            <Input
                                id="password" type="password" required
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                className="rounded-none mt-2 h-11" data-testid="login-password"
                            />
                        </div>
                        <Button type="submit" disabled={busy} className="w-full rounded-none bg-ikb hover:bg-[#001f7a] text-white h-11" data-testid="login-submit">
                            {busy ? "Signing in..." : "Sign in →"}
                        </Button>
                    </form>
                    <div className="mt-8 text-sm text-foreground/80">
                        New here?{" "}
                        <Link to="/signup" className="text-ikb font-medium hover:underline" data-testid="goto-signup">
                            Create a company account
                        </Link>
                    </div>
                    <div className="mt-2 text-sm text-foreground/80">
                        Forgot your password?{" "}
                        <Link to="/forgot-password" className="text-ikb font-medium hover:underline" data-testid="goto-forgot">
                            Reset it →
                        </Link>
                    </div>
                    <div className="mt-2 text-sm text-foreground/80">
                        Looking for a job?{" "}
                        <Link to="/seeker/login" className="text-ikb font-medium hover:underline" data-testid="goto-seeker-login">
                            Sign in as a candidate →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
